// Shared collapsible criteria bar for visuals & dashboards.
//
// Extracted (behaviour-preserving) from the visual preview modal's criteria
// pattern so every surface that runs a visual/dashboard gets the same UX: a
// thin toolbar row with a collapse toggle + a one-line summary of the applied
// criteria when collapsed, and a full-width, scrollable criteria FORM when
// expanded. The host supplies its own action controls (Run / Apply / SQL / row
// count) through the `actions` slot, and owns the collapsed state so it can
// auto-collapse after a successful run.
//
// Internals are inline-styled (no app `btn` CSS classes) so the standalone
// runtime — which doesn't ship those classes — renders identically.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { ChevronDown, ChevronRight, SlidersHorizontal } from 'lucide-react'
import VisualCriteriaForm from './VisualCriteriaForm'
import { issuesFor } from './criteriaFormat'
import { tw } from '../testHooks'
import type { CriteriaIssue, VisualCriteriaPromptVar, VisualCriteriaRuntime } from './VisualCriteriaForm'

export interface CriteriaBarProps {
  project: string
  promptVars: VisualCriteriaPromptVar[]
  /** Current input values keyed by prompt name. */
  values: Record<string, string>
  onChange: (name: string, val: string) => void
  /** Fired when the form is submitted (Enter) — usually re-runs the query. */
  onSubmit: () => void
  runtime: VisualCriteriaRuntime
  collapsed: boolean
  onToggleCollapsed: () => void
  /** Host action controls rendered on the right of the toolbar row. */
  actions?: ReactNode
  /** Findings of the last run's checks (`criteriaErrors` + `criteriaNotices`,
   *  or the host's own required check). Each shows under the criterion it
   *  names; a warning — the run went ahead, so the bar may be folded — and
   *  anything naming no visible criterion also show under the toolbar. */
  issues?: CriteriaIssue[]
  /** 'band' (default) — the thin toolbar strip every visual surface uses.
   *  'card' — a dashboard SCREEN's filter card (DashboardFilterCard): sits on
   *  the card's own surface, a labelled "Filters" toggle, and the applied
   *  criteria as chips that open the form. */
  look?: 'band' | 'card'
}

// A criterion's value as a folded chip says it: the LIKE wildcard `%` (the
// "match anything" default) and a picklist list holding every option read
// "All"; a picklist value reads its option's display text, item by item for
// a list. '' = no value (no chip).
function chipValue(pv: VisualCriteriaPromptVar, raw: unknown): string {
  const value = String(raw ?? '').trim()
  if (value === '') return ''
  if (/^%+$/.test(value)) return 'All'
  const options = pv.options || []
  if (!options.length) return value
  const whole = options.find((o) => String(o.data) === value)
  if (whole) return String(whole.display || whole.data)
  const picked = value.split(',').map((v) => v.trim()).filter(Boolean)
  const all = new Set(options.map((o) => String(o.data)))
  if (picked.length >= all.size && [...all].every((d) => picked.includes(d))) return 'All'
  return picked.map((v) => {
    const hit = options.find((o) => String(o.data) === v)
    return hit ? String(hit.display || hit.data) : v
  }).join(', ')
}

/** Most rows of chips the folded card shows before "+N more". */
const FOLDED_CHIP_ROWS = 2

interface FoldedChip { name: string; label: string; value: string }

/**
 * The folded card's chips, at most FOLDED_CHIP_ROWS rows of them: whatever
 * would wrap past the last row is summed up in a "+N more" chip at its end.
 * Measured after layout (before paint, so nothing flashes) and again whenever
 * the card's width changes. A chip opens the form at its own criterion
 * (`onPick(name)`); "+N more" opens it at the top (`onPick(null)`).
 */
function FoldedChips({ chips, onPick }: {
  chips: FoldedChip[]
  onPick: (name: string | null) => void
}) {
  const boxRef = useRef<HTMLDivElement | null>(null)
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([])
  const moreRef = useRef<HTMLButtonElement | null>(null)
  // How many chips fit with the "+N more" chip; null = all of them (measuring).
  const [shown, setShown] = useState<number | null>(null)
  // Bumped on a width change so the measurement runs again even when `shown`
  // is already null (setting null to null would not re-render).
  const [, setWidthTick] = useState(0)
  const signature = chips.map((c) => `${c.name}=${c.value}`).join('\u0001')

  // The chips the last measurement was for — new chips are measured afresh.
  const measuredFor = useRef(signature)
  // A new width: measure from scratch.
  useEffect(() => {
    const box = boxRef.current
    if (!box || typeof ResizeObserver === 'undefined') return undefined
    let width = box.clientWidth
    const ro = new ResizeObserver(() => {
      if (box.clientWidth !== width) {
        width = box.clientWidth
        setShown(null)
        setWidthTick((n) => n + 1)
      }
    })
    ro.observe(box)
    return () => ro.disconnect()
  }, [])

  useLayoutEffect(() => {
    if (measuredFor.current !== signature) {
      measuredFor.current = signature
      if (shown !== null) { setShown(null); return }
    }
    const tops = (els: (HTMLElement | null)[]) =>
      [...new Set(els.filter(Boolean).map((el) => (el as HTMLElement).offsetTop))].sort((x, y) => x - y)
    if (shown === null) {
      const rows = tops(chipRefs.current.slice(0, chips.length))
      if (rows.length <= FOLDED_CHIP_ROWS) return
      const lastTop = rows[FOLDED_CHIP_ROWS - 1]
      const fit = chipRefs.current.slice(0, chips.length)
        .filter((el) => el && el.offsetTop <= lastTop).length
      setShown(Math.max(1, fit))
      return
    }
    // "+N more" must sit on the last allowed row too; give up a chip until it does.
    const more = moreRef.current
    if (!more || shown <= 1) return
    const rows = tops(chipRefs.current.slice(0, shown))
    const lastTop = rows[Math.min(FOLDED_CHIP_ROWS, rows.length) - 1]
    if (rows.length > FOLDED_CHIP_ROWS || more.offsetTop > lastTop) setShown(shown - 1)
  })

  const visible = shown === null ? chips : chips.slice(0, shown)
  const hidden = chips.length - visible.length
  return (
    <div ref={boxRef} style={{
      position: 'relative', flex: '1 1 280px', minWidth: 0, minHeight: 34, paddingTop: 2,
      boxSizing: 'border-box', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
    }}>
      {visible.map((c, i) => (
        <button
          key={c.name}
          ref={(el) => { chipRefs.current[i] = el }}
          type="button"
          onClick={() => onPick(c.name)}
          title={`${c.label}: ${c.value} — click to change`}
          style={cardChipStyle}
        >
          <span style={{ color: 'var(--color-text-secondary)' }}>{c.label}</span>
          <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.value}</span>
        </button>
      ))}
      {hidden > 0 && (
        <button
          ref={moreRef}
          type="button"
          onClick={() => onPick(null)}
          title={chips.slice(visible.length).map((c) => `${c.label}: ${c.value}`).join('\n')}
          style={{ ...cardChipStyle, fontWeight: 600, color: 'var(--color-accent)' }}
        >
          +{hidden} more
        </button>
      )}
    </div>
  )
}

// look="card": no surfaces of its own (the card supplies one), a divider only
// between the toolbar and what opens under it.
// Top-aligned: when the chips wrap onto several rows the toggle and the
// host's Apply stay level with the first one instead of floating mid-block.
const cardToolbarStyle: CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start',
  padding: '8px 12px', flexShrink: 0,
}
const cardToggleStyle: CSSProperties = {
  height: 34, display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
  padding: '0 10px', borderRadius: 8, border: 'none', background: 'transparent',
  color: 'var(--color-text)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
}
const cardChipStyle: CSSProperties = {
  height: 30, display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%',
  padding: '0 12px', borderRadius: 15, border: '1px solid var(--color-border)',
  background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
  fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
}
const cardFormRegionStyle: CSSProperties = {
  flexShrink: 0, padding: '14px 16px 16px', borderTop: '1px solid var(--color-border)',
}
const cardStripStyle: CSSProperties = {
  display: 'grid', gap: 2, padding: '8px 16px', fontSize: 13,
  borderTop: '1px solid var(--color-border)', flexShrink: 0,
}

const stripStyle: CSSProperties = {
  display: 'grid', gap: 2, padding: '6px 12px', fontSize: 13,
  borderBottom: '1px solid var(--color-border)', flexShrink: 0,
}

const toolbarStyle: CSSProperties = {
  display: 'flex', gap: 10, alignItems: 'center',
  padding: '8px 12px', borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))', flexShrink: 0,
}

const toggleBtnStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
  background: 'none', border: '1px solid var(--color-border)', borderRadius: 6,
  padding: '3px 6px', cursor: 'pointer', color: 'var(--color-text-secondary)',
}

const formRegionStyle: CSSProperties = {
  flexShrink: 0, maxHeight: '40%', overflowY: 'auto',
  padding: '14px 16px', borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))',
}

export default function CriteriaBar({
  project, promptVars, values, onChange, onSubmit, runtime,
  collapsed, onToggleCollapsed, actions, issues, look = 'band',
}: CriteriaBarProps) {
  const card = look === 'card'
  // A prompt the tenant hid always runs on its default: not a filter the
  // user sees or counts.
  const visibleVars = useMemo(() => promptVars.filter((pv) => !pv.hidden), [promptVars])
  const hasFilters = visibleVars.length > 0
  // "Label: value" pairs shown when collapsed — only the ones with a value,
  // said the way a person reads them (chipValue).
  const filterChips = useMemo(
    () => visibleVars
      .map((pv) => ({ name: pv.name, label: pv.label, value: chipValue(pv, values[pv.name]) }))
      .filter((c) => c.value !== ''),
    [visibleVars, values],
  )
  // A chip clicked while folded: open the form and put the caret in that
  // criterion's field (null = just open it).
  const formRef = useRef<HTMLDivElement | null>(null)
  const [focusField, setFocusField] = useState<string | null>(null)
  const openAt = (name: string | null) => {
    setFocusField(name)
    if (collapsed) onToggleCollapsed()
  }
  useEffect(() => {
    if (collapsed || !focusField) return undefined
    // After the form has rendered its fields.
    const raf = requestAnimationFrame(() => {
      const region = formRef.current
      const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(focusField) : focusField
      const field = region?.querySelector<HTMLElement>(`[data-tw-field="${escaped}"]`)
      const target = field && (field.matches('input, select, textarea')
        ? field
        : field.querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea, button'))
      if (target) {
        target.focus()
        target.scrollIntoView?.({ block: 'nearest' })
      }
      setFocusField(null)
    })
    return () => cancelAnimationFrame(raf)
  }, [collapsed, focusField])

  // What the fields cannot show: notices (visible even while folded) and any
  // finding that names no visible criterion.
  const stripIssues = useMemo(
    () => (issues || []).filter((i) => (i.severity || 'E') !== 'E'
      || !visibleVars.some((pv) => issuesFor([i], pv.name).length > 0)
      || collapsed),
    [issues, visibleVars, collapsed],
  )
  const labelOf = (param: string) =>
    visibleVars.find((pv) => issuesFor([{ param, message: '' }], pv.name).length)?.label || ''

  return (
    <>
      <div style={card ? cardToolbarStyle : toolbarStyle}>
        {hasFilters && card && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            {...tw('action', 'toggle_criteria')}
            title={collapsed ? 'Show filters' : 'Hide filters'}
            aria-expanded={!collapsed}
            style={cardToggleStyle}
          >
            <SlidersHorizontal size={16} />
            Filters
            <ChevronDown size={16} style={{
              color: 'var(--color-text-secondary)', transition: 'transform 150ms ease',
              transform: collapsed ? 'none' : 'rotate(180deg)',
            }} />
          </button>
        )}
        {hasFilters && !card && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            {...tw('action', 'toggle_criteria')}
            title={collapsed ? 'Show criteria' : 'Hide criteria'}
            aria-label={collapsed ? 'Show criteria' : 'Hide criteria'}
            style={toggleBtnStyle}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            <SlidersHorizontal size={14} />
          </button>
        )}

        {/* One-line applied-criteria summary while collapsed (band look). */}
        {/* look="card": the applied criteria as chips, at most two rows; a
            chip opens the form at its own criterion. */}
        {card && (
          hasFilters && collapsed && filterChips.length > 0 ? (
            <FoldedChips chips={filterChips} onPick={openAt} />
          ) : (
            <div style={{ flex: '1 1 280px', minWidth: 0, minHeight: 34, display: 'flex', alignItems: 'center' }}>
              {hasFilters && collapsed && (
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {visibleVars.length} {visibleVars.length === 1 ? 'filter' : 'filters'} · showing all
                </span>
              )}
            </div>
          )
        )}
        {!card && (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          {hasFilters && collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', flexShrink: 0 }}>
                {visibleVars.length} criteri{visibleVars.length === 1 ? 'on' : 'a'}
              </span>
              {filterChips.length > 0 && (
                <span style={{
                  fontSize: 14, color: 'var(--color-text-secondary)', minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {filterChips.map((c) => `${c.label}: ${c.value}`).join('  •  ')}
                </span>
              )}
            </div>
          )}
        </div>
        )}

        {actions && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </div>

      {stripIssues.length > 0 && (
        <div {...tw('msg', 'criteria-notes')} role="status" style={card ? cardStripStyle : stripStyle}>
          {stripIssues.map((i, n) => {
            const label = labelOf(i.param)
            return (
              <span key={n} style={{
                color: (i.severity || 'E') === 'E' ? 'var(--color-error)' : 'var(--color-text-secondary)',
              }}>
                {label ? `${label}: ` : ''}{i.message}
              </span>
            )
          })}
        </div>
      )}

      {hasFilters && !collapsed && (
        <div ref={formRef} style={card ? cardFormRegionStyle : formRegionStyle}>
          <VisualCriteriaForm
            project={project}
            promptVars={promptVars}
            values={values}
            onChange={onChange}
            onSubmit={onSubmit}
            runtime={runtime}
            issues={issues}
          />
        </div>
      )}
    </>
  )
}
